import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import FooterSection from '@/pages/home/components/FooterSection';
import SteelLinkLogo from '@/components/feature/SteelLinkLogo';

const productCategories = [
  'Construction & Building Steel',
  'Roofing & Cladding',
  'Plumbing & Piping',
  'Industrial & Structural Steel',
  'Wire & Mesh',
  'Other / Not Sure',
];

const paymentMethods = ['T/T (Bank Wire)', 'L/C (Letter of Credit)', 'Not Sure Yet'];
const portOptions = ['FOB China Port', 'CIF Destination Port', 'CFR Destination Port', 'Not Sure'];

export default function QuotePage() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('sending');
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const fields = ['input_text', 'email', 'phone', 'company', 'country', 'product_category', 'product_details', 'quantity', 'destination_port', 'payment_method', 'timeline', 'description'];
    fields.forEach((field) => {
      const el = form.querySelector(`[name="${field}"]`) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
      if (el) data.append(field, el.value);
    });
    try {
      await fetch('https://readdy.ai/api/form/d779fb2qo7glkto3e4eg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-white">
        <Navbar onQuoteClick={() => {}} />
        <div className="min-h-screen flex items-center justify-center px-6 pt-24">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: 'rgba(10,35,66,0.08)' }}>
              <i className="ri-checkbox-circle-line text-4xl" style={{ color: '#0a2342' }} />
            </div>
            <h1 className="text-2xl font-black text-gray-900 mb-3">Quote Request Sent!</h1>
            <p className="text-gray-600 text-sm mb-2">Thank you! We have received your request and will send you a detailed FOB quote within <strong>2 business hours</strong>.</p>
            <p className="text-gray-500 text-xs mb-8">Check your email inbox (and spam folder just in case).</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/" className="px-6 py-3 text-white font-black rounded-lg text-sm" style={{ backgroundColor: '#0a2342' }}>
                Back to Home
              </Link>
              <a href="https://wa.me/8617671262710" target="_blank" rel="noopener noreferrer" className="px-6 py-3 bg-green-500 text-white font-black rounded-lg text-sm flex items-center justify-center gap-2">
                <i className="ri-whatsapp-line" /> Follow Up on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar onQuoteClick={() => {}} />

      {/* Hero */}
      <section className="pt-28 pb-10 md:pt-32 md:pb-14" style={{ backgroundColor: '#0a2342' }}>
        <div className="max-w-[1420px] mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <span className="text-xs font-black tracking-widest uppercase mb-3 block" style={{ color: '#7eb8f7' }}>Free Quote</span>
              <h1 className="text-3xl md:text-5xl font-black text-white mb-3 leading-tight">
                Get Your Steel Price<br />in 2 Hours
              </h1>
              <p className="text-white/70 text-sm max-w-lg">
                Fill in the form below. Our team will find the best FOB price from our verified mill partners and send you a full quote — fast, no obligation.
              </p>
            </div>
            <div className="hidden md:flex flex-col gap-3 flex-shrink-0">
              {[
                { icon: 'ri-time-line', text: 'Reply within 2 hours' },
                { icon: 'ri-shield-check-line', text: 'Mill certified products' },
                { icon: 'ri-ship-line', text: 'FOB from major Chinese ports' },
                { icon: 'ri-bank-card-line', text: 'L/C & T/T accepted' },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-3 text-white/80 text-sm">
                  <i className={`${item.icon} text-base`} style={{ color: '#7eb8f7' }} />
                  {item.text}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-6">
          <form data-readdy-form onSubmit={handleSubmit} className="space-y-6">

            {/* Section 1: Contact Info */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h2 className="text-base font-black text-gray-900 mb-5 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full text-white text-xs font-black flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#0a2342' }}>1</span>
                Your Contact Information
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">Full Name *</label>
                  <input name="input_text" type="text" required placeholder="e.g. Ahmed Al-Rashidi" className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">Email Address *</label>
                  <input name="email" type="email" required placeholder="your@email.com" className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">Phone / WhatsApp *</label>
                  <input name="phone" type="text" required placeholder="+971 50 000 0000" className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">Company Name</label>
                  <input name="company" type="text" placeholder="Your company name" className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">Country *</label>
                  <input name="country" type="text" required placeholder="e.g. Nigeria, UAE, India..." className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400" />
                </div>
              </div>
            </div>

            {/* Section 2: Product Info */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h2 className="text-base font-black text-gray-900 mb-5 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full text-white text-xs font-black flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#0a2342' }}>2</span>
                Product Details
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">Product Category *</label>
                  <select name="product_category" required className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400 bg-white">
                    <option value="">Select category...</option>
                    {productCategories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">Quantity (MT) *</label>
                  <input name="quantity" type="text" required placeholder="e.g. 50 MT, 200 MT" className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">Product Name & Specifications *</label>
                  <input name="product_details" type="text" required placeholder="e.g. ERW Black Pipe 2 inch SCH40, H-Beam 200x200mm, Rebar HRB400 12mm..." className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400" />
                </div>
              </div>
            </div>

            {/* Section 3: Shipping & Payment */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h2 className="text-base font-black text-gray-900 mb-5 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full text-white text-xs font-black flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#0a2342' }}>3</span>
                Shipping &amp; Payment
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">Destination Port</label>
                  <input name="destination_port" type="text" placeholder="e.g. Lagos, Dubai, Mumbai..." className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">Shipping Terms</label>
                  <select name="payment_method" className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400 bg-white">
                    <option value="">Select terms...</option>
                    {portOptions.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">Payment Method</label>
                  <select name="timeline" className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400 bg-white">
                    <option value="">Select payment...</option>
                    {paymentMethods.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">When Do You Need It?</label>
                  <input name="description" type="text" placeholder="e.g. Within 30 days, ASAP, Q2 2026..." className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400" />
                </div>
              </div>
            </div>

            {/* Submit */}
            {status === 'error' && (
              <p className="text-red-500 text-sm text-center">Something went wrong. Please try again or contact us on WhatsApp.</p>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="submit"
                disabled={status === 'sending'}
                className="flex-1 text-white py-4 rounded-xl text-sm font-black cursor-pointer disabled:opacity-60 transition-all active:scale-95 whitespace-nowrap"
                style={{ backgroundColor: '#0a2342' }}
              >
                <i className="ri-send-plane-line mr-2" />
                {status === 'sending' ? 'Sending Your Request...' : 'Submit Quote Request'}
              </button>
              <a
                href="https://wa.me/8617671262710"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-6 py-4 bg-green-500 hover:bg-green-600 text-white font-black rounded-xl text-sm transition-all whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-base" /> WhatsApp Instead
              </a>
            </div>

            <p className="text-center text-xs text-gray-400">
              <i className="ri-lock-line mr-1" />Your information is private and will never be shared.
            </p>
          </form>

          {/* Trust strip */}
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { icon: 'ri-time-line', title: 'Reply in 2 Hours', desc: 'Mon–Fri, 9AM–6PM CST' },
              { icon: 'ri-shield-check-line', title: 'No Obligation', desc: 'Free quote, no commitment' },
              { icon: 'ri-award-line', title: 'Mill Certified', desc: 'SGS & TUV inspected' },
              { icon: 'ri-global-line', title: '50+ Countries', desc: 'Worldwide FOB delivery' },
            ].map((item) => (
              <div key={item.title} className="text-center p-4 bg-gray-50 rounded-xl border border-gray-100">
                <i className={`${item.icon} text-xl mb-2 block`} style={{ color: '#0a2342' }} />
                <div className="text-xs font-bold text-gray-900">{item.title}</div>
                <div className="text-xs text-gray-500 mt-0.5">{item.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FooterSection />
    </div>
  );
}
